import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setDataTheme } from "../../data/redux/themeSettingSlice";
import { useSidebarData } from "../../data/json/horizontalSidebar";
import HelpModal from "./HelpModal";
import AccountModal from "./AccountModal";
import Submenu from "./Submenu";
import { all_routes } from "../../../feature-module/router/all_routes";
import { API_URL } from "../../../environment";
import { useBrand } from "../../../contexts/BrandContext";
import { multicontaAtivo, dentroDeFrameDeConta } from "../../session/multiconta";

// Notificação de anotação interna do chat (mencionados)
interface AnotacaoNotif {
  id: number;
  id_chat: number;
  fone: string | number;
  id_time: number;
  id_cliente: number;
  id_user_autor: number;
  autor_nome: string;
  texto: string;
  contato_nome: string;
  lida: boolean;
  created_at: string;
}

const HorizontalSidebar = () => {
  const routes = all_routes;
  const { logo_url, nome_produto } = useBrand();
  const location = useLocation();
  const dispatch = useDispatch();
  const dataTheme = useSelector((state: any) => state.themeSetting.dataTheme);
  const isDark = dataTheme === "dark";
  const sidebarData = useSidebarData();
  const [subOpen, setSubOpen] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const foneIns = localStorage.getItem("fone_ins");
  const [submenuCoords, setSubmenuCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const isAdminMaster = localStorage.getItem("isAdminMaster") === "true";
  const nomeTimeLogin = localStorage.getItem("nome_time");
  const navRef = useRef<HTMLDivElement>(null);
  const navScrollRef = useRef<HTMLElement>(null);

  // Indicador de conexão do número na topbar (oficial vs uazapi) — polling 60s
  const [numeroConexao, setNumeroConexao] = useState<{
    tipo: "oficial" | "uazapi" | null;
    numero: string;
    status: "verificando" | "verde" | "amarelo" | "vermelho" | "desconectado";
  }>({
    tipo: null,
    numero: foneIns && foneIns !== "null" ? foneIns : "",
    status: "verificando",
  });
  // Multi-número: lista de números oficiais do login + saúde de cada (p/ o dropdown do header)
  const [numerosOficiais, setNumerosOficiais] = useState<
    { ins_nome: string; numero: string; saude: string }[]
  >([]);
  const [dropdownNumAberto, setDropdownNumAberto] = useState(false);
  const dropdownNumRef = useRef<HTMLDivElement>(null);
  // Cor do status do número na Meta: vermelho SÓ quando a saúde é vermelha,
  // laranja SÓ quando amarela; qualquer outro caso (verde, verificando,
  // desconectado, etc.) fica sempre verde.
  const classeSaude = (s: string) =>
    s === "vermelho" ? "unhealthy" : s === "amarelo" ? "limited" : "";
  const labelSaude = (s: string) =>
    s === "verde" ? "Saudável" : s === "amarelo" ? "Atenção" : s === "vermelho" ? "Em risco" : "—";
  const piorSaude = (arr: { saude: string }[]) =>
    arr.some((n) => n.saude === "vermelho")
      ? "vermelho"
      : arr.some((n) => n.saude === "amarelo")
      ? "amarelo"
      : arr.some((n) => n.saude === "verde")
      ? "verde"
      : "desconectado";

  useEffect(() => {
    const instOficial = localStorage.getItem("instanciaoficial");
    const instanceName = localStorage.getItem("instanceName");
    const id_user = localStorage.getItem("id_user");
    const id_cliente = localStorage.getItem("id_cliente");
    const fone = localStorage.getItem("fone_ins");
    const ehOficial = !!(instOficial && instOficial !== "0");
    const ehUazapi = !!(instanceName && instanceName !== "null" && instanceName !== "undefined");
    if (!ehOficial && !ehUazapi) {
      setNumeroConexao({ tipo: null, numero: "", status: "desconectado" });
      return;
    }
    let cancelado = false;
    const verificar = async () => {
      try {
        if (ehOficial) {
          // Multi-número: saúde de TODOS os números oficiais do login (dropdown se >1)
          const r = await fetch(`${API_URL}/status_numeros_oficiais`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_email: localStorage.getItem("user_email"), id_cliente }),
          });
          const j = await r.json();
          if (cancelado) return;
          const nums: { ins_nome: string; numero: string; saude: string }[] =
            j && Array.isArray(j.numeros) ? j.numeros : [];
          if (nums.length > 0) {
            setNumerosOficiais(nums);
            setNumeroConexao({
              tipo: "oficial",
              numero: nums.length === 1 ? nums[0].numero : `${nums.length} números`,
              status: piorSaude(nums),
            });
          } else {
            // Fallback: endpoint de 1 número (backend antigo / sem números no plural)
            setNumerosOficiais([]);
            const r2 = await fetch(`${API_URL}/status_numero_oficial`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id_user, id_cliente, ins_nome: instOficial }),
            });
            const j2 = await r2.json();
            if (cancelado) return;
            setNumeroConexao({
              tipo: "oficial",
              numero: (j2 && j2.numero) || fone || "",
              status: (j2 && j2.saude) || (j2 && j2.ativo ? "verde" : "desconectado"),
            });
          }
        } else {
          // Uazapi (não oficial): consulta connectionStatus da instância
          const r = await fetch(`${API_URL}/pegarinstancias`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ instanceName }),
          });
          const arr = await r.json();
          if (cancelado) return;
          const st = Array.isArray(arr) ? arr[0]?.connectionStatus : null;
          const conectado = st === "open" || st === "connected";
          setNumerosOficiais([]);
          setNumeroConexao({
            tipo: "uazapi",
            numero: fone && fone !== "null" ? fone : (instanceName || ""),
            status: conectado ? "verde" : "desconectado",
          });
        }
      } catch {
        if (!cancelado) setNumeroConexao((prev) => ({ ...prev, status: "desconectado" }));
      }
    };
    verificar();
    const id = setInterval(verificar, 60000);
    return () => {
      cancelado = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fecha o dropdown de números ao clicar fora
  useEffect(() => {
    if (!dropdownNumAberto) return;
    const onDoc = (e: MouseEvent) => {
      if (dropdownNumRef.current && !dropdownNumRef.current.contains(e.target as Node)) {
        setDropdownNumAberto(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [dropdownNumAberto]);

  // ── Notificações de anotações internas (habilitado para todos os grupos) ──
  const ANOTACOES_HABILITADO = !!localStorage.getItem("id_grupo");
  const [notifs, setNotifs] = useState<AnotacaoNotif[]>([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const carregarNotificacoes = React.useCallback(() => {
    const id_user = localStorage.getItem("id_user");
    if (!ANOTACOES_HABILITADO || !id_user) return;
    fetch(`${API_URL}/anotacao_notificacoes?id_user=${encodeURIComponent(id_user)}`)
      .then((r) => r.json())
      .then((d) => {
        setNotifs(Array.isArray(d?.notificacoes) ? d.notificacoes : []);
        setNaoLidas(Number(d?.nao_lidas) || 0);
      })
      .catch((e) => console.error("Erro ao carregar notificações de anotação:", e));
  }, [ANOTACOES_HABILITADO]);

  useEffect(() => {
    if (!ANOTACOES_HABILITADO) return;
    carregarNotificacoes();
    const intervalo = setInterval(carregarNotificacoes, 25000);
    return () => clearInterval(intervalo);
  }, [ANOTACOES_HABILITADO, carregarNotificacoes]);

  // Fecha o dropdown de notificações ao clicar fora
  useEffect(() => {
    if (!showNotifDropdown) return;
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showNotifDropdown]);

  const formatNotifHora = (iso: string) => {
    try {
      return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const marcarTodasLidas = () => {
    const id_user = localStorage.getItem("id_user");
    if (!id_user) return;
    setNaoLidas(0);
    setNotifs((prev) => prev.map((n) => ({ ...n, lida: true })));
    fetch(`${API_URL}/anotacao_notificacoes/marcar_lida`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id_user, todas: true }),
    }).catch((e) => console.error("Erro ao marcar todas lidas:", e));
  };

  const abrirNotificacao = (n: AnotacaoNotif) => {
    // marca como lida (otimista)
    if (!n.lida) {
      setNaoLidas((v) => Math.max(0, v - 1));
      setNotifs((prev) => prev.map((x) => (x.id === n.id ? { ...x, lida: true } : x)));
      fetch(`${API_URL}/anotacao_notificacoes/marcar_lida`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: n.id }),
      }).catch((e) => console.error("Erro ao marcar notificação lida:", e));
    }
    // sinaliza para o /chat abrir a conversa (mesmo que não seja do usuário)
    localStorage.setItem("open_anotacao_fone", String(n.fone));
    localStorage.setItem("open_anotacao_id_time", String(n.id_time));
    setShowNotifDropdown(false);
    window.location.href = routes.chat || "/chat";
  };

  useEffect(() => {
    const storedEmail = localStorage.getItem("user_email");
    setUserEmail(storedEmail);
  }, []);

  // Close submenu on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // Check if click is inside nav OR inside a submenu portal
      const isInsideNav = navRef.current && navRef.current.contains(target);
      const isInsideSubmenu = (target as Element).closest?.(".horizontal-submenu-portal");
      if (!isInsideNav && !isInsideSubmenu) {
        setSubOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Menu rolável: a roda do mouse (que é vertical) vira rolagem horizontal, senão
  // quem não tem trackpad só alcançaria os itens escondidos arrastando a scrollbar.
  // Listener manual porque o React registra `wheel` como passivo — ali o
  // preventDefault seria ignorado e a página rolaria junto.
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    // O submenu é um portal com position: fixed ancorado na posição do botão —
    // ao rolar o menu ele descolaria do item, então fecha.
    const onScroll = () => setSubOpen((prev) => (prev === null ? prev : null));
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("scroll", onScroll);
    };
  }, []);

  const toggleSubmenu = (menuValue: string) => {
    setSubOpen((prev) => (prev === menuValue ? null : menuValue));
  };

  const handleLogout = () => {
    localStorage.removeItem("id_cliente");
    localStorage.removeItem("user_email");
    localStorage.removeItem("nome_time");
    localStorage.removeItem("isConnected");
    window.location.href = "/entrar";
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!userEmail) return "?";
    const parts = userEmail.split("@")[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return userEmail.substring(0, 2).toUpperCase();
  };

  // Check if route is active
  const isRouteActive = (route?: string) => {
    if (!route || route === "#") return false;
    return location.pathname === route;
  };

  const isMenuActive = (item: any) => {
    if (isRouteActive(item.route)) return true;
    if (item.subMenus) {
      return item.subMenus.some((sub: any) =>
        isRouteActive(sub.route) ||
        (sub.children && sub.children.some((child: any) => isRouteActive(child.route)))
      );
    }
    return false;
  };

  return (
    <div className="unified-topbar" id="unified-topbar" ref={navRef}>
      <style>{`
        /* Reset: prevent any inherited sidebar/header styles from interfering */
        .unified-topbar,
        .unified-topbar * {
          box-sizing: border-box !important;
        }

        .unified-topbar {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          height: 64px !important;
          min-height: 64px !important;
          max-height: 64px !important;
          background: #ffffff !important;
          border-bottom: 1px solid #e5e7eb !important;
          display: flex !important;
          flex-wrap: nowrap !important;
          flex-direction: row !important;
          align-items: center !important;
          padding: 0 24px !important;
          z-index: 1000 !important;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
          transition: background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
          overflow: visible;
          margin: 0 !important;
          border-right: 0 !important;
          width: 100% !important;
        }

        /* ── Logo Area ── */
        .topbar-logo {
          display: flex;
          align-items: center;
          flex-shrink: 0;
          margin-right: 20px;
        }

        .topbar-logo img {
          height: 34px;
          width: auto;
        }

        /* ── Navigation ── */
        /* Rolável em QUALQUER largura: antes era overflow:hidden e os itens que
           não cabiam ficavam inacessíveis em telas menores (cliente com tela
           pequena não alcançava os últimos botões). */
        .topbar-nav {
          display: flex;
          align-items: center;
          gap: 2px;
          flex: 1 1 0;
          min-width: 0;
          justify-content: flex-start;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior-x: contain;
          scrollbar-width: thin;
          scrollbar-color: rgba(100, 116, 139, 0.3) transparent;
        }

        /* Centraliza quando o menu cabe e libera a rolagem quando não cabe.
           Um justify-content: center cortaria o início de forma irrecuperável —
           margem auto colapsa para 0 no overflow, então nada fica inalcançável. */
        .topbar-nav > .topbar-nav-item:first-child {
          margin-left: auto;
        }
        .topbar-nav > .topbar-nav-item:last-child {
          margin-right: auto;
        }

        .topbar-nav::-webkit-scrollbar {
          height: 4px;
        }
        .topbar-nav::-webkit-scrollbar-track {
          background: transparent;
        }
        .topbar-nav::-webkit-scrollbar-thumb {
          background: rgba(100, 116, 139, 0.28);
          border-radius: 4px;
        }
        .topbar-nav:hover::-webkit-scrollbar-thumb {
          background: rgba(100, 116, 139, 0.45);
        }

        .topbar-time-pill {
          display: flex;
          align-items: center;
          gap: 6px;
          flex: 0 0 auto;
          max-width: 200px;
          height: 30px;
          margin: 0 6px;
          padding: 0 10px;
          border-radius: 15px;
          font-size: 12px;
          font-weight: 600;
          color: var(--brand-primary, #007e94);
          background: rgba(var(--brand-primary-rgb, 0, 126, 148), 0.08);
          border: 1px solid rgba(var(--brand-primary-rgb, 0, 126, 148), 0.18);
          white-space: nowrap;
        }
        .topbar-time-pill span {
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .topbar-time-pill svg {
          width: 14px;
          height: 14px;
          flex: 0 0 auto;
        }
        [data-theme="dark"] .topbar-time-pill {
          color: #4dd8e8;
          background: rgba(77, 216, 232, 0.1);
          border-color: rgba(77, 216, 232, 0.2);
        }
        @media (max-width: 900px) {
          .topbar-time-pill { display: none; }
        }
        .topbar-nav-item {
          list-style: none;
          position: relative;
        }

        .topbar-nav-link {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 7px 14px;
          border-radius: 10px;
          font-size: 13.5px;
          font-weight: 500;
          color: #64748b;
          text-decoration: none;
          transition: all 0.2s ease;
          white-space: nowrap;
          cursor: pointer;
          border: 1px solid transparent;
        }

        .topbar-nav-link:hover {
          color: var(--brand-primary, #007e94);
          background: rgba(var(--brand-primary-rgb, 0, 126, 148), 0.06);
        }

        .topbar-nav-link.active {
          color: var(--brand-primary, #007e94);
          background: rgba(var(--brand-primary-rgb, 0, 126, 148), 0.08);
          border-color: rgba(var(--brand-primary-rgb, 0, 126, 148), 0.12);
          font-weight: 600;
        }

        .topbar-nav-link svg {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
          stroke-width: 1.8;
        }

        .topbar-nav-link .nav-arrow {
          width: 14px;
          height: 14px;
          margin-left: -2px;
          opacity: 0.5;
          transition: transform 0.2s ease;
        }

        .topbar-nav-link .nav-arrow.open {
          transform: rotate(180deg);
        }

        /* ── User Area ── */
        .topbar-user-area {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
          margin-left: 20px;
          white-space: nowrap;
        }

        .topbar-status {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 600;
          padding: 5px 12px;
          border-radius: 20px;
          background: rgba(3, 201, 90, 0.08);
          color: #03C95A;
        }

        .topbar-status-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #03C95A;
          animation: statusPulse 2s ease-in-out infinite;
        }

        .topbar-status.disconnected {
          background: rgba(148, 163, 184, 0.08);
          color: #94a3b8;
        }

        .topbar-status.disconnected .topbar-status-dot {
          background: #94a3b8;
          animation: none;
        }

        /* Amarelo: qualidade YELLOW ou envio LIMITED na Meta */
        .topbar-status.limited {
          background: rgba(245, 158, 11, 0.1);
          color: #d97706;
        }
        .topbar-status.limited .topbar-status-dot {
          background: #f59e0b;
        }

        /* Vermelho: qualidade RED ou número BLOCKED na Meta */
        .topbar-status.unhealthy {
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
        }
        .topbar-status.unhealthy .topbar-status-dot {
          background: #ef4444;
        }

        /* Dropdown de múltiplos números (multi-número) */
        .topbar-num-dropdown {
          position: relative;
        }
        .topbar-num-dropdown .topbar-status {
          cursor: pointer;
          border: none;
          font-family: inherit;
        }
        .topbar-num-panel {
          position: absolute;
          top: calc(100% + 6px);
          right: 0;
          min-width: 240px;
          background: #fff;
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 10px;
          box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
          padding: 6px;
          z-index: 1100;
        }
        .topbar-num-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 7px 10px;
          border-radius: 8px;
          font-size: 12.5px;
          font-weight: 600;
          color: #03c95a;
        }
        .topbar-num-item.limited { color: #d97706; }
        .topbar-num-item.unhealthy { color: #dc2626; }
        .topbar-num-item.disconnected { color: #94a3b8; }
        .topbar-num-item .topbar-status-dot { background: #03c95a; }
        .topbar-num-item.limited .topbar-status-dot { background: #f59e0b; }
        .topbar-num-item.unhealthy .topbar-status-dot { background: #ef4444; }
        .topbar-num-item.disconnected .topbar-status-dot { background: #94a3b8; animation: none; }
        .topbar-num-item-fone {
          flex: 1;
          color: #1f2937;
          white-space: nowrap;
        }
        .topbar-num-item-saude {
          font-size: 11px;
          opacity: 0.9;
        }
        [data-theme="dark"] .topbar-num-panel {
          background: #1e293b;
          border-color: rgba(255, 255, 255, 0.12);
        }
        [data-theme="dark"] .topbar-num-item-fone { color: #e2e8f0; }

        @keyframes statusPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        .topbar-help-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 7px 14px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: transparent;
          color: #64748b;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .topbar-help-btn:hover {
          border-color: var(--brand-primary, #007e94);
          color: var(--brand-primary, #007e94);
          background: rgba(var(--brand-primary-rgb, 0, 126, 148), 0.04);
        }

        .topbar-help-btn svg {
          width: 15px;
          height: 15px;
        }

        /* ── Dark Mode Toggle ── */
        .topbar-theme-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          background: transparent;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #94a3b8;
          padding: 0;
        }

        .topbar-theme-toggle:hover {
          border-color: var(--brand-primary, #007e94);
          color: var(--brand-primary, #007e94);
          background: rgba(var(--brand-primary-rgb, 0, 126, 148), 0.04);
        }

        .topbar-theme-toggle svg {
          width: 18px;
          height: 18px;
        }

        /* ── Divider ── */
        .topbar-divider {
          width: 1px;
          height: 28px;
          background: #e5e7eb;
          margin: 0 4px;
          transition: background-color 0.3s ease;
        }

        /* ── User Avatar ── */
        .topbar-avatar-group {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: default;
        }

        .topbar-avatar {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: linear-gradient(135deg, var(--brand-primary, #007e94) 0%, #009ab5 100%);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.5px;
          flex-shrink: 0;
        }

        .topbar-user-info {
          display: flex;
          flex-direction: column;
          line-height: 1.2;
        }

        .topbar-user-email {
          font-size: 13px;
          font-weight: 600;
          color: #1e293b;
          max-width: 140px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          transition: color 0.3s ease;
        }

        .topbar-user-role {
          font-size: 11px;
          color: #94a3b8;
          font-weight: 500;
          transition: color 0.3s ease;
        }

        /* ── Logout ── */
        .topbar-logout {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          border: 1px solid transparent;
          background: transparent;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #94a3b8;
          padding: 0;
        }

        .topbar-logout:hover {
          border-color: rgba(231, 13, 13, 0.15);
          color: #E70D0D;
          background: rgba(231, 13, 13, 0.04);
        }

        .topbar-logout svg {
          width: 18px;
          height: 18px;
        }

        /* ═══════════════════════════════════════ */
        /* DARK MODE                               */
        /* ═══════════════════════════════════════ */
        [data-theme="dark"] .unified-topbar {
          background: #1a1a2e !important;
          border-bottom-color: #2a2a3e !important;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
        }

        [data-theme="dark"] .topbar-nav {
          scrollbar-color: rgba(136, 146, 168, 0.35) transparent;
        }

        [data-theme="dark"] .topbar-nav::-webkit-scrollbar-thumb {
          background: rgba(136, 146, 168, 0.3);
        }

        [data-theme="dark"] .topbar-nav:hover::-webkit-scrollbar-thumb {
          background: rgba(136, 146, 168, 0.5);
        }

        [data-theme="dark"] .topbar-nav-link {
          color: #8892a8;
        }

        [data-theme="dark"] .topbar-nav-link:hover {
          color: #4dd8e8;
          background: rgba(var(--brand-primary-rgb, 0, 126, 148), 0.1);
        }

        [data-theme="dark"] .topbar-nav-link.active {
          color: #4dd8e8;
          background: rgba(var(--brand-primary-rgb, 0, 126, 148), 0.12);
          border-color: rgba(var(--brand-primary-rgb, 0, 126, 148), 0.2);
        }

        [data-theme="dark"] .topbar-help-btn {
          border-color: #2a2a3e;
          color: #8892a8;
        }

        [data-theme="dark"] .topbar-help-btn:hover {
          border-color: #4dd8e8;
          color: #4dd8e8;
          background: rgba(77, 216, 232, 0.06);
        }

        [data-theme="dark"] .topbar-theme-toggle {
          border-color: #2a2a3e;
          color: #8892a8;
        }

        [data-theme="dark"] .topbar-theme-toggle:hover {
          border-color: #4dd8e8;
          color: #4dd8e8;
        }

        [data-theme="dark"] .topbar-divider {
          background: #2a2a3e;
        }

        [data-theme="dark"] .topbar-user-email {
          color: #e2e8f0;
        }

        [data-theme="dark"] .topbar-user-role {
          color: #64748b;
        }

        [data-theme="dark"] .topbar-logout {
          color: #64748b;
        }

        [data-theme="dark"] .topbar-logout:hover {
          color: #f87171;
          border-color: rgba(248, 113, 113, 0.15);
          background: rgba(248, 113, 113, 0.06);
        }

        [data-theme="dark"] .topbar-status {
          background: rgba(3, 201, 90, 0.1);
        }

        [data-theme="dark"] .topbar-status.disconnected {
          background: rgba(148, 163, 184, 0.1);
        }

        [data-theme="dark"] .topbar-status.limited {
          background: rgba(245, 158, 11, 0.14);
          color: #fbbf24;
        }

        [data-theme="dark"] .topbar-status.unhealthy {
          background: rgba(239, 68, 68, 0.14);
          color: #f87171;
        }

        /* ═══════════════════════════════════════ */
        /* RESPONSIVE                              */
        /* ═══════════════════════════════════════ */
        @media (max-width: 1400px) {
          .topbar-user-info {
            display: none;
          }
        }

        @media (max-width: 1200px) {
          .unified-topbar {
            padding: 0 16px !important;
          }
          .topbar-logo {
            margin-right: 12px;
          }
          .topbar-logo img {
            height: 28px;
          }
          .topbar-help-btn span {
            display: none;
          }
          .topbar-nav-link span {
            display: none;
          }
          .topbar-nav-link {
            padding: 8px 10px;
          }
          .topbar-nav-link svg:first-child {
            width: 20px;
            height: 20px;
          }
          .topbar-status span:not(.topbar-status-dot) {
            display: none;
          }
          .topbar-user-area {
            margin-left: 10px;
            gap: 6px;
          }
        }

        @media (max-width: 1080px) {
          .unified-topbar {
            padding: 0 12px !important;
          }
          .topbar-logo {
            margin-right: 8px;
          }
          .topbar-logo img {
            height: 24px;
          }
          .topbar-nav {
            gap: 0;
          }
          .topbar-nav-link {
            padding: 7px 8px;
          }
          .topbar-nav-link svg:first-child {
            width: 18px;
            height: 18px;
          }
          .topbar-nav-link .nav-arrow {
            display: none;
          }
          .topbar-status {
            display: none;
          }
          .topbar-help-btn {
            padding: 6px 8px;
          }
          .topbar-theme-toggle,
          .topbar-avatar,
          .topbar-logout {
            width: 32px;
            height: 32px;
          }
          .topbar-divider {
            height: 22px;
            margin: 0 2px;
          }
          .topbar-user-area {
            margin-left: 6px;
            gap: 4px;
          }
        }

        @media (max-width: 992px) {
          .unified-topbar {
            padding: 0 8px !important;
            height: 52px !important;
            min-height: 52px !important;
            max-height: 52px !important;
          }
          /* Logo escondida no mobile: ficava cortada/espremida. Sobra espaço p/ a página. */
          .topbar-logo {
            display: none !important;
          }
          .topbar-nav {
            gap: 0 !important;
            overflow-x: auto !important;
            overflow-y: hidden !important;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .topbar-nav::-webkit-scrollbar {
            display: none;
          }
          .topbar-nav-link {
            padding: 6px 7px !important;
          }
          .topbar-nav-link svg:first-child {
            width: 18px !important;
            height: 18px !important;
          }
          .topbar-nav-link span,
          .topbar-nav-link .nav-arrow {
            display: none !important;
          }
          .topbar-help-btn {
            padding: 5px 7px !important;
            border: none !important;
          }
          .topbar-help-btn span {
            display: none !important;
          }
          .topbar-user-area {
            margin-left: 4px !important;
            gap: 2px !important;
          }
          .topbar-theme-toggle,
          .topbar-avatar,
          .topbar-logout {
            width: 30px !important;
            height: 30px !important;
            font-size: 12px !important;
          }
          .topbar-divider {
            display: none !important;
          }
          .topbar-status {
            display: none !important;
          }
        }

        @media (max-width: 480px) {
          .unified-topbar {
            padding: 0 6px !important;
            height: 48px !important;
            min-height: 48px !important;
            max-height: 48px !important;
          }
          .topbar-logo img {
            height: 18px !important;
          }
          .topbar-nav-link {
            padding: 5px 6px !important;
          }
          .topbar-nav-link svg:first-child {
            width: 16px !important;
            height: 16px !important;
          }
          .topbar-help-btn {
            padding: 5px 6px !important;
          }
          .topbar-theme-toggle,
          .topbar-avatar,
          .topbar-logout {
            width: 28px !important;
            height: 28px !important;
          }
        }
      `}</style>

      {/* ── Logo ── */}
      <Link to={routes.leadsList} className="topbar-logo">
        <img src={logo_url} alt={nome_produto} />
      </Link>

      {/* ── Navigation ── */}
      <nav className="topbar-nav" ref={navScrollRef}>
        {sidebarData.map((item, index) => (
          <div key={index} className="topbar-nav-item">
            <Link
              to={item.route || "#"}
              className={`topbar-nav-link ${isMenuActive(item) ? "active" : ""} ${subOpen === item.menuValue ? "active" : ""}`}
              onClick={(e) => {
                // Rota externa (http/https): abre em nova aba (não navega no React Router).
                if (item.route && /^https?:\/\//i.test(item.route)) {
                  e.preventDefault();
                  window.open(item.route, "_blank", "noopener,noreferrer");
                  return;
                }
                if (item.subMenus) {
                  e.preventDefault();
                  const rect = e.currentTarget.getBoundingClientRect();
                  setSubmenuCoords({ top: rect.bottom + 8, left: rect.left });
                  toggleSubmenu(item.menuValue);
                }
              }}
            >
              {/* Inline SVG icons per menu item */}
              {item.icon === "calendar-event" && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  <path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/><path d="M8 18h.01"/><path d="M12 18h.01"/>
                </svg>
              )}
              {item.icon === "message-circle" && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                </svg>
              )}
              {item.icon === "users" && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              )}
              {item.icon === "chart-bar" && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/>
                </svg>
              )}
              {item.icon === "settings" && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              )}
              {item.icon === "layout" && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/>
                </svg>
              )}
              {item.icon === "pencil" && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                </svg>
              )}
              {item.icon === "alert-triangle" && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              )}
              {item.icon === "photo" && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                </svg>
              )}
              {item.icon === "bolt" && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                </svg>
              )}
              <span>{item.menuValue}</span>
              {item.subMenus && !item.isLoading && (
                <svg className={`nav-arrow ${subOpen === item.menuValue ? "open" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              )}
            </Link>
            {item.subMenus && subOpen === item.menuValue && (
              <Submenu
                items={item.subMenus}
                style={{
                  position: "fixed",
                  top: submenuCoords.top,
                  left: submenuCoords.left,
                  zIndex: 99999,
                  padding: "0.5rem",
                }}
                className="horizontal-submenu-portal"
                onClose={() => setSubOpen(null)}
                onSelect={(selectedItem) => {
                  if (selectedItem.isPlaceholder) return;
                  if (selectedItem.params) {
                    if (selectedItem.params.funnelName) localStorage.setItem("nome_pai", selectedItem.params.funnelName);
                    if (selectedItem.params.teamId) localStorage.setItem("id_cliente_filter", selectedItem.params.teamId);
                    if (selectedItem.params.userId) localStorage.setItem("id_user_filter", selectedItem.params.userId);
                  } else {
                    localStorage.setItem("nome_pai", selectedItem.menuValue);
                    localStorage.removeItem("id_cliente_filter");
                    localStorage.removeItem("id_user_filter");
                  }
                  if (selectedItem.route && selectedItem.route !== "#") {
                    window.location.href = selectedItem.route;
                  } else {
                    window.location.reload();
                  }
                }}
              />
            )}
          </div>
        ))}
      </nav>

      {/* Time do login, ao lado de "Administração". Admin geral (master) enxerga todos os
          times, então não mostra nada. O nome vem do /login (nome_time). */}
      {!isAdminMaster && nomeTimeLogin && (
        <div className="topbar-time-pill" title={`Seu time: ${nomeTimeLogin}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <span>{nomeTimeLogin}</span>
        </div>
      )}

      {/* ── User Area ── */}
      <div className="topbar-user-area">
        {/* Connection Status — 1 número (pill) ou vários (dropdown) c/ saúde da Meta */}
        {numerosOficiais.length > 1 ? (
          <div className="topbar-num-dropdown" ref={dropdownNumRef}>
            <button
              type="button"
              className={`topbar-status ${classeSaude(numeroConexao.status)}`}
              onClick={() => setDropdownNumAberto((v) => !v)}
              title="Números oficiais — clique para ver a saúde de cada um na Meta"
            >
              <span className="topbar-status-dot" />
              {`${numerosOficiais.length} números`}
              <span style={{ fontSize: 9, marginLeft: 1 }}>{dropdownNumAberto ? "▲" : "▼"}</span>
            </button>
            {dropdownNumAberto && (
              <div className="topbar-num-panel">
                {numerosOficiais.map((n) => (
                  <div key={n.ins_nome} className={`topbar-num-item ${classeSaude(n.saude)}`}>
                    <span className="topbar-status-dot" />
                    <span className="topbar-num-item-fone">{n.numero}</span>
                    <span className="topbar-num-item-saude">{labelSaude(n.saude)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          numeroConexao.numero && (
            <div
              className={`topbar-status ${classeSaude(numeroConexao.status)}`}
              title={
                numeroConexao.status === "verde"
                  ? "Número saudável na Meta"
                  : numeroConexao.status === "amarelo"
                  ? "Atenção: qualidade amarela ou envio limitado na Meta"
                  : numeroConexao.status === "vermelho"
                  ? "Número em risco na Meta (qualidade vermelha ou bloqueado)"
                  : numeroConexao.status === "verificando"
                  ? "Verificando…"
                  : "Número desconectado / inativo"
              }
            >
              <span className="topbar-status-dot" />
              {numeroConexao.tipo === "oficial"
                ? `API Oficial: ${numeroConexao.numero}`
                : numeroConexao.numero}
            </div>
          )
        )}

        {/* Help */}
        <button className="topbar-help-btn" onClick={() => setShowHelpModal(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <span>Ajuda</span>
        </button>

        {/* Notificações de anotações (habilitado para todos os grupos) */}
        {ANOTACOES_HABILITADO && (
          <div ref={notifRef} style={{ position: "relative" }}>
            <button
              className="topbar-help-btn"
              onClick={() => { setShowNotifDropdown((v) => !v); if (!showNotifDropdown) carregarNotificacoes(); }}
              title="Notificações"
              style={{ position: "relative" }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {naoLidas > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    minWidth: 16,
                    height: 16,
                    padding: "0 4px",
                    borderRadius: 8,
                    background: "#e63946",
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                  }}
                >
                  {naoLidas > 99 ? "99+" : naoLidas}
                </span>
              )}
            </button>

            {showNotifDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 10px)",
                  right: 0,
                  width: 360,
                  maxWidth: "90vw",
                  background: isDark ? "#111b21" : "#fff",
                  border: isDark ? "1px solid #2a3942" : "1px solid #e3e8ef",
                  borderRadius: 12,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.22)",
                  zIndex: 2000,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    borderBottom: isDark ? "1px solid #2a3942" : "1px solid #eef1f5",
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: 14, color: isDark ? "#e9edef" : "#1a1a2e" }}>
                    Notificações
                  </span>
                  {naoLidas > 0 && (
                    <button
                      onClick={marcarTodasLidas}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "var(--brand-primary, #007e94)",
                        fontSize: 12.5,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Marcar todas como lidas
                    </button>
                  )}
                </div>

                <div style={{ maxHeight: 380, overflowY: "auto" }}>
                  {notifs.length === 0 ? (
                    <div style={{ padding: "28px 16px", textAlign: "center", color: isDark ? "#8696a0" : "#94a3b8", fontSize: 13 }}>
                      Nenhuma notificação
                    </div>
                  ) : (
                    notifs.map((n) => (
                      <button
                        key={n.id}
                        onClick={() => abrirNotificacao(n)}
                        style={{
                          display: "flex",
                          gap: 10,
                          width: "100%",
                          textAlign: "left",
                          border: "none",
                          borderBottom: isDark ? "1px solid #1c2730" : "1px solid #f2f4f7",
                          background: n.lida ? "transparent" : (isDark ? "#15212a" : "#f6fbfc"),
                          padding: "12px 16px",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? "#1c2730" : "#eef6f8")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = n.lida ? "transparent" : (isDark ? "#15212a" : "#f6fbfc"))}
                      >
                        <span
                          style={{
                            width: 38,
                            height: 38,
                            flexShrink: 0,
                            borderRadius: "50%",
                            background: "var(--brand-primary, #007e94)",
                            color: "#fff",
                            fontSize: 14,
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {(n.autor_nome || "?").charAt(0).toUpperCase()}
                        </span>
                        <span style={{ minWidth: 0, flex: 1 }}>
                          <span style={{ display: "block", fontSize: 13, color: isDark ? "#e9edef" : "#1a1a2e", lineHeight: 1.35 }}>
                            <strong style={{ color: "var(--brand-primary, #007e94)" }}>{n.autor_nome}</strong> mencionou você em uma anotação
                          </span>
                          <span
                            style={{
                              display: "block",
                              fontSize: 12.5,
                              color: isDark ? "#9aa6ad" : "#667085",
                              marginTop: 2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {n.texto}
                          </span>
                          <span style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                            <span style={{ fontSize: 11.5, color: isDark ? "#6b7780" : "#98a2b3" }}>
                              {n.contato_nome || n.fone} · {formatNotifHora(n.created_at)}
                            </span>
                            {!n.lida && (
                              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", flexShrink: 0 }} />
                            )}
                          </span>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Theme Toggle */}
        <button
          className="topbar-theme-toggle"
          onClick={() => dispatch(setDataTheme(isDark ? "light" : "dark"))}
          title={isDark ? "Modo claro" : "Modo escuro"}
        >
          {isDark ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
              <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          )}
        </button>

        <div className="topbar-divider" />

        {/* User Avatar + Info */}
        {userEmail && (
          <div
            className="topbar-avatar-group"
            role="button"
            tabIndex={0}
            title="Minha conta"
            style={{ cursor: "pointer" }}
            onClick={() => setShowAccountModal(true)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowAccountModal(true); }}
          >
            <div className="topbar-avatar">{getUserInitials()}</div>
            <div className="topbar-user-info">
              <span className="topbar-user-email">{userEmail}</span>
              <span className="topbar-user-role">
                {isAdmin ? "Administrador" : "Operador"}
              </span>
            </div>
          </div>
        )}

        {/* Multi-conta: abre o shell que roda várias contas ao mesmo tempo.
            Só aparece com o recurso ligado (interruptor no public/index.html) e nunca
            quando JÁ estamos dentro de um frame de conta — senão daria para aninhar
            shell dentro de shell. */}
        {multicontaAtivo() && !dentroDeFrameDeConta() && (
          <button
            className="topbar-logout"
            onClick={() => { window.location.href = "/contas"; }}
            title="Usar várias contas ao mesmo tempo"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/>
              <rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>
            </svg>
          </button>
        )}

        {/* Logout */}
        <button className="topbar-logout" onClick={handleLogout} title="Sair">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </button>
      </div>

      <HelpModal show={showHelpModal} onHide={() => setShowHelpModal(false)} />
      <AccountModal
        show={showAccountModal}
        onHide={() => setShowAccountModal(false)}
        email={userEmail}
        isAdmin={isAdmin}
      />
    </div>
  );
};

export default HorizontalSidebar;
